import { lazy, Suspense, useEffect } from 'react';
import { HomeLayout } from 'app-pages/pages/_sharedLayouts/HomeLayout';
import CoreChooseMember from 'app-pages/pages/choose_member/CoreChooseMember';
import { ConsultLayout } from 'app-pages/pages/consult/components/ConsultLayout';
import IndexPage from 'app-pages/pages/IndexPage';
import Landing from 'app-pages/pages/landing/Landing';
import { ProtectedRoute } from 'components/auth0/AuthProvider';
import { AccountPatientsProvider } from 'contexts/AccountPatientsContext';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';

import { useUserExperience } from './api/account/useUserExperience';
import { FeatureFlags, useFlag } from './hooks/useFlag';
import { AspRoutes } from './routes/AspRoutes';
import LoginHint from './routes/LoginHint';
import { NpRoutes } from './routes/NpRoutes';

/* eslint-disable prettier/prettier */
const UpdateChildPage = lazy(() => import('app-pages/pages/account/UpdateChild'));
const UpdateMemberPCPPage = lazy(() => import('app-pages/pages/account/UpdateMemberPCP'));
const ExclusionComponent = lazy(() => import('app-pages/pages/account/ExclusionComponent'));
const ExclusionConfirmComponent = lazy(() => import('app-pages/pages/account/ExclusionConfirmComponent'));
const RelationshipPageComponent = lazy(() => import('app-pages/pages/account/RelationshipPageComponent'));
const AccountCreatePage = lazy(() => import('app-pages/pages/login/AccountCreatePage'));
const HomePage = lazy(() => import('app-pages/pages/home/HomePage'));
const AccountSettingsPage = lazy(() => import('app-pages/pages/settings/AccountSettingsPage'));
const AccountAndMemberSettingsPage = lazy(() => import('app-pages/pages/settings/AccountAndMemberSettingsPage'));
const PreferencesSettingsPage = lazy(() => import('app-pages/pages/settings/PreferencesSettingsPage'));
const AccountUpdatePage = lazy(() => import('app-pages/pages/settings/AccountUpdatePage'));
const PatientSettingsPage = lazy(() => import('app-pages/pages/settings/PatientSettingsPage'));
const PlansComponent = lazy(() => import('app-pages/pages/plans/PlansComponent'));
const SettingsPage = lazy(() => import('app-pages/pages/settings/SettingsPage'));
const StartConsultPage = lazy(() => import('app-pages/pages/consult/ConsultStartPage'));
const AssessmentWelcomePage = lazy(() => import('app-pages/pages/consult/AssessmentWelcomePage'));
const MedicalHistorySurveyPage = lazy(() => import('app-pages/pages/consult/medical-history/MedicalHistorySurveyPage'));
const DemographicsReviewPage = lazy(() => import('app-pages/pages/consult/demographics/DemographicsReviewPage'));
const PatientVitalsPageComponent = lazy(() => import('app-pages/pages/consult/PatientVitalsPageComponent'));

const ConsultVideoPage = lazy(() => import('app-pages/pages/consult/video/ConsultVideoPage'));
const ConsultSurveyPage = lazy(() => import('app-pages/pages/consult/survey/ConsultSurveyPage'));
const MessagingPage = lazy(() => import('app-pages/pages/messaging/MessagingPage'));
const ChatChannelPage = lazy(() => import('app-pages/pages/messaging/chat/ChatChannelPage'));
const ConsultResultsPage = lazy(() => import('app-pages/pages/consult/results/ConsultResultsPage'));
const NotThereYet = lazy(() => import('app-pages/pages/consult/NotThere'));

const SubscribePage = lazy(() => import('app-pages/pages/consult/SubscribePage'));
const StripeSuccessPage = lazy(() => import('app-pages/pages/consult/StripeSuccessPage'));
const StripeSuccessPageC3HS = lazy(() => import('app-pages/pages/consult/StripeSuccessPageC3HS'));
const EligibilityCheckPage = lazy(() => import('app-pages/pages/consult/EligibilityCheckPage'));

const ScheduleFirstAppointmentPage = lazy(
  () => import('app-pages/pages/consult/appointments/ScheduleFirstAppointmentPage'),
);
const PlanPage = lazy(() => import('app-pages/pages/plans/PlanPage'));
const WelcomeScreen = lazy(() => import('app-pages/pages/consult/eligibility/WelcomeScreen'));
const AccountVerifyPage = lazy(() => import('app-pages/pages/account/VerifyPage'));
const HistoryReview = lazy(() => import('app-pages/pages/consult/HistoryReviewPage'));
const WelcomePage = lazy(() => import('app-pages/pages/welcome/WelcomePage'));
const NoShowCancellationPolicy = lazy(() => import('app-pages/pages/consult/eligibility/NoShowCancellationPolicy'));
const ConsultCancellationPage = lazy(
  () => import('app-pages/pages/consult/eligibility/ginaConsent/ConsultCancellationPage'),
);
const CommunityPage = lazy(() => import('app-pages/pages/community/CommunityPage'));
const AppointmentsPage = lazy(() => import('app-pages/pages/appointments/AppointmentsPage'));

const AgreementsPage = lazy(() => import('app-pages/pages/consult/agreements/AgreementsPage'));

const AgreementsRenewalPage = lazy(() => import('app-pages/pages/consult/agreements/AgreementsRenewalPage'));

const DocumentsPage = lazy(() => import('app-pages/pages/settings/DocumentsPage'));

export function MemberPrivateRouter() {
  const { data: userExperience } = useUserExperience();
  const push = useNavigate();
  const displayInformedConsents = useFlag(FeatureFlags.DisplayInformedConsents);
  const aspLeadIntakeEnabled = useFlag(FeatureFlags.AspLeadIntakeEnabled, true);

  let requireSession = true;
  if (requireSession && AUTH_BYPASS && global.location.pathname === '/login') {
    requireSession = false;
  }

  useEffect(() => {
    if (!userExperience?.landingPage) return;

    if (global.location.pathname !== userExperience.landingPage) {
      push(userExperience.landingPage, { replace: true });
    }
  }, [userExperience?.landingPage]); // eslint-disable-line react-hooks/exhaustive-deps
  // Do not add global.location.pathname to dependencies, as it will cause an infinite loop.

  return (
    <Suspense>
      <Routes>
        <Route index element={<ProtectedRoute component={IndexPage} />} />
        <Route path="/account/create/:program?" element={<ProtectedRoute component={AccountCreatePage} />} />
        <Route path="landing" element={<ProtectedRoute component={Landing} />} />
        <Route path="np/*" element={<NpRoutes />} />
        <Route path="/login-hint/:program" element={<LoginHint />} />
        {/* lead onboarding */}

        {aspLeadIntakeEnabled && <Route path="/asp/intake/:id" Component={AspRoutes} />}

        {/* dashboard and related pages */}
        <Route
          element={
            <AccountPatientsProvider>
              <Outlet />
            </AccountPatientsProvider>
          }
        >
          {/* home pages-with navigation */}
          <Route element={<HomeLayout />}>
            <Route path="home" element={<ProtectedRoute component={HomePage} />} />
            <Route
              path="/appointments/:memberId?/:appointmentId?"
              element={<ProtectedRoute component={AppointmentsPage} />}
            />
            <Route path="account/update" element={<ProtectedRoute component={AccountUpdatePage} />} />
            <Route
              path="account/update-profile"
              element={<ProtectedRoute component={AccountAndMemberSettingsPage} />}
            />
            <Route path="account/verify" element={<ProtectedRoute component={AccountVerifyPage} />} />
            <Route path="settings" element={<ProtectedRoute component={SettingsPage} />} />
            <Route path="plans" element={<ProtectedRoute component={PlansComponent} />} />
            <Route path="plans/:selectedPatientId" element={<ProtectedRoute component={PlansComponent} />} />
            <Route path="settings/account" element={<ProtectedRoute component={AccountSettingsPage} />} />
            <Route path="settings/preferences" element={<ProtectedRoute component={PreferencesSettingsPage} />} />
            {displayInformedConsents && (
              <Route
                path="settings/documents/:selectedPatientId?"
                element={<ProtectedRoute component={DocumentsPage} />}
              />
            )}
            <Route path="settings/:patientId" element={<ProtectedRoute component={PatientSettingsPage} />} />
            <Route path="messaging" element={<ProtectedRoute component={MessagingPage} />} />
            <Route path="messaging/:chatId" element={<ProtectedRoute component={ChatChannelPage} />} />
            <Route path="/consult/:consultId/treatment-plan" element={<ProtectedRoute component={PlanPage} />} />
            <Route path="messaging" element={<ProtectedRoute component={MessagingPage} />} />
            <Route path="messaging/:chatId" element={<ProtectedRoute component={ChatChannelPage} />} />
            <Route path="/consult/:consultId/treatment-plan" element={<ProtectedRoute component={PlanPage} />} />
            <Route path="/community" element={<ProtectedRoute component={CommunityPage} />} />
          </Route>

          {/* home pages - no navigation */}
          <Route element={<HomeLayout hideNav />}>
            <Route
              path="account/relationship/:program?"
              element={
                <Suspense>
                  <ProtectedRoute component={RelationshipPageComponent} />
                </Suspense>
              }
            />

            <Route path="core/choose-member" element={<ProtectedRoute component={CoreChooseMember} />} />
            {/* TODO: remove add-child or implement some sort of redirect component */}
            <Route path="account/add-child" element={<ProtectedRoute component={UpdateChildPage} />} />

            <Route path="account/add-member/:program?" element={<ProtectedRoute component={UpdateChildPage} />} />
            <Route
              path="account/update-child/:patientId/:program?"
              element={<ProtectedRoute component={UpdateChildPage} />}
            />
            <Route path="account/update-member/:patientId" element={<ProtectedRoute component={UpdateChildPage} />} />
            <Route
              path="account/update-member-pcp/:patientId"
              element={<ProtectedRoute component={UpdateMemberPCPPage} />}
            />
            <Route
              path="account/exclusion/:patientId?/:program?"
              element={<ProtectedRoute component={ExclusionComponent} />}
            />
            <Route
              path="account/exclusion-confirm"
              element={<ProtectedRoute component={ExclusionConfirmComponent} />}
            />
          </Route>
        </Route>

        {/* Agreement page - the consult ID is just a passthrough */}
        <Route path="member/:memberId/agreements/:consultId?" element={<ProtectedRoute component={AgreementsPage} />} />
        <Route path="agreements/renewal" element={<ProtectedRoute component={AgreementsRenewalPage} />} />

        <Route path="consult/:consultId" element={<ConsultLayout />}>
          <Route path="start" element={<ProtectedRoute component={StartConsultPage} />} />
          <Route path="welcome" element={<ProtectedRoute component={AssessmentWelcomePage} />} />
          <Route path="demographics-review" element={<ProtectedRoute component={DemographicsReviewPage} />} />
          <Route path="survey/:surveyId" element={<ProtectedRoute component={ConsultSurveyPage} />} />

          <Route path="patient-vitals" element={<PatientVitalsPageComponent />} />
          <Route path="history" element={<ProtectedRoute component={MedicalHistorySurveyPage} />} />
          <Route path="results" element={<ProtectedRoute component={ConsultResultsPage} />} />
          <Route path="subscription" element={<ProtectedRoute component={SubscribePage} />} />
          <Route path="stripe_cancel" element={<Navigate to="../subscription" replace />} />
          <Route path="stripe_success" element={<ProtectedRoute component={StripeSuccessPage} />} />
          <Route path="stripe_success_c3hs" element={<ProtectedRoute component={StripeSuccessPageC3HS} />} />
          <Route
            path="schedule/first-appointment"
            element={<ProtectedRoute component={ScheduleFirstAppointmentPage} />}
          />
          <Route path="videos" element={<ProtectedRoute component={ConsultVideoPage} />} />
          <Route path="not-there-yet" element={<ProtectedRoute component={NotThereYet} />} />
          <Route path="history-review" element={<ProtectedRoute component={HistoryReview} />} />
        </Route>

        {/* eligibility pages */}
        <Route path="eligibility/:consultId" element={<ConsultLayout />}>
          <Route path="check/:program?" element={<ProtectedRoute component={EligibilityCheckPage} />} />
          <Route
            path="settings/check/:program?"
            element={<ProtectedRoute component={() => <EligibilityCheckPage settingsFlow={true} />} />}
          />
          <Route path="welcome" element={<ProtectedRoute component={WelcomeScreen} />} />
          <Route path="no-show-and-cancellation-policy" element={<NoShowCancellationPolicy />} />
        </Route>

        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="consult/:consultId/consult-cancellation" element={<ConsultCancellationPage />} />
        <Route path="*" element={<ProtectedRoute component={IndexPage} />} />
      </Routes>
    </Suspense>
  );
}
